/**
 * Central enterprise lead capture (/demo). The lead is captured FIRST; the calendar is shown only after
 * capture succeeds. Scheduling status starts as "Lead Captured — Not Yet Scheduled"; the CRM should flip it
 * to "Demo Scheduled" when the calendar reports a booking.
 */
import { icon } from '../../components/icons';
import { scheduleStep } from '../../components/schedule-step';
import { isDemoIntent, type DemoIntent } from '../../content/demo-intents';
import { escapeHtml } from '../../lib/html';
import { $, prefersReducedMotion, requireElement } from '../../lib/dom';
import { setSubmitting, showDeliveryFailure } from '../forms/form-status';
import { buildPayload, sendForm, type FormFields } from './delivery/send-form';
import { buildFallbackMailto } from './delivery/email-fallback';
import { getLeadContext } from './lead-context';

const HEADER_OFFSET_PX = 96;

export interface LeadRecord extends FormFields {
  readonly fullName: string;
  readonly email: string;
  readonly company: string;
  readonly teamSize: string;
  readonly challenge: string;
  readonly contactTime: string;
  readonly intent: string;
  readonly page_source: string;
  readonly cta_source: string;
  readonly section_source: string;
  readonly submitted_at: string;
  readonly scheduling_status: string;
}

const FORM_TYPES: Readonly<Record<DemoIntent, string>> = {
  demo: 'Enterprise demo request',
  sales: 'Talk to sales',
  'enterprise-contact': 'Enterprise contact',
};

export function buildLeadRecord(fields: FormFields, intent: DemoIntent, now: Date = new Date()): LeadRecord {
  const context = getLeadContext();
  return {
    fullName: fields.fullName ?? '',
    email: fields.email ?? '',
    company: fields.company ?? '',
    teamSize: fields.teamSize ?? '',
    challenge: fields.challenge ?? '',
    contactTime: fields.contactTime ?? '',
    intent: intent === 'enterprise-contact' ? 'enterprise_contact' : intent,
    page_source: context.page_source,
    cta_source: context.cta_source,
    section_source: context.section_source,
    submitted_at: now.toISOString(),
    scheduling_status: 'Lead Captured — Not Yet Scheduled',
  };
}

export const firstName = (fullName: string): string => fullName.trim().split(/\s+/)[0] || 'there';

function showScheduleStep(wrap: HTMLElement, name: string, intent: DemoIntent): void {
  wrap.innerHTML = scheduleStep(name, intent !== 'demo');
  const step = requireElement('.sched', wrap);
  step.focus();
  $('[data-wait]', wrap)?.addEventListener('click', () => {
    step.innerHTML = `<span class="ok-ic">${icon('check')}</span><h2 class="h-cap">Thanks, ${escapeHtml(name)}.</h2><p class="muted">A member of the ReTax360 team will reach out. If you'd like to pick a time later, you can return to this page anytime.</p>`;
    step.focus();
  });
  window.scrollTo({
    top: wrap.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET_PX,
    behavior: prefersReducedMotion() ? 'auto' : 'smooth',
  });
}

/** Captures the lead, then shows step 2 (scheduling). Bots caught by the honeypot see success; nothing is sent. */
export async function submitLead(form: HTMLFormElement, fields: FormFields, isBot: boolean): Promise<void> {
  const wrap = requireElement('#lead-wrap');
  const intent: DemoIntent = isDemoIntent(wrap.dataset.intent) ? wrap.dataset.intent : 'demo';
  const lead = buildLeadRecord(fields, intent);
  const formType = FORM_TYPES[intent];
  const restoreButton = setSubmitting(form, 'Saving…');
  try {
    if (!isBot) await sendForm(lead, formType);
  } catch {
    restoreButton(); // entered data is kept
    showDeliveryFailure(requireElement('.err-summary', form), {
      emailHref: buildFallbackMailto(buildPayload(lead, formType), formType),
      keptEntries: true,
    });
    return;
  }
  showScheduleStep(wrap, firstName(lead.fullName), intent);
}
